/**
 * Oud Collection: WebGL2 pieces shared by the hero (oud-collection.js, OudHeroEngine) and the light
 * over the sections below it (OudLight). Same engine as the US site (app/components/oud/oudGl.ts,
 * oudAmbient.ts).
 *
 * - LightTrail: the pointer's light, in a half-resolution ping-pong buffer (R = trail light,
 *   G = click burst) that is blurred, drifted and decayed every frame, so light smears like warm
 *   smoke and fades out.
 * - AmbientLight: slow, grainy waves of light always drifting in the background, which the pointer
 *   pushes around like smoke (a displacement field it writes into, relaxing back every frame).
 */
(function () {
  const VERTEX_SHADER = `#version 300 es
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

  /** "Hash without sine" (Dave Hoskins, MIT): evenly spread, no visible pattern. */
  const HASH_GLSL = `
float hash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}`;

  /** Sandy light from a trail sample: grainier where the pointer clicked. */
  const TRAIL_GLOW_GLSL = `
float trailGlow(vec2 trail, vec2 grainCell, float time) {
  float n = hash(grainCell + fract(time * 7.0) * 97.0);
  float sandy = mix(1.0, 0.35 + 1.3 * n, 0.6 + 0.35 * trail.g);
  return (trail.r + trail.g * 0.8) * sandy;
}`;

  /** Grain for the upsampled ambient light: a fine sand, much lighter than the trail's. Needs HASH_GLSL. */
  const AMBIENT_GRAIN_GLSL = `
float ambientGrain(vec2 grainCell, float time) {
  return 0.8 + 0.4 * hash(grainCell * 0.73 + fract(time * 5.0) * 61.0);
}`;

  const TRAIL_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uPrev;
uniform vec2 uTexel;
uniform float uAspect;
uniform float uTime;
uniform float uDecay;
uniform float uShift; // content scroll since last frame, in uv (fixed canvases keep light on the content)
uniform vec2 uFrom;
uniform vec2 uTo;
uniform float uMove;
uniform float uRadius;
uniform vec4 uBurst; // xy position, z strength, w radius

float segmentDistance(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
  return length(pa - ba * h);
}

void main() {
  vec2 aspect = vec2(uAspect, 1.0);
  vec2 q = vUv * aspect;

  // the old light drifts like warm smoke: a slow swirl plus a slight rise
  vec2 swirl = vec2(
    sin(q.y * 9.0 + uTime * 0.8) + 0.5 * sin(q.y * 17.0 - uTime * 1.3),
    cos(q.x * 8.0 - uTime * 0.7) + 0.5 * cos(q.x * 15.0 + uTime * 1.1)
  );
  vec2 uv = vUv - swirl * uTexel * 0.6 - vec2(0.0, uTexel.y * 0.35 + uShift);

  vec4 c = texture(uPrev, uv) * 0.4;
  c += texture(uPrev, uv + vec2(uTexel.x, 0.0)) * 0.15;
  c += texture(uPrev, uv - vec2(uTexel.x, 0.0)) * 0.15;
  c += texture(uPrev, uv + vec2(0.0, uTexel.y)) * 0.15;
  c += texture(uPrev, uv - vec2(0.0, uTexel.y)) * 0.15;
  c = max(c * vec4(uDecay, uDecay * 0.985, 0.0, 0.0) - 1.0 / 255.0, 0.0);

  float d = segmentDistance(q, uFrom * aspect, uTo * aspect);
  float core = exp(-d * d / (uRadius * uRadius));
  float tail = uRadius * 0.35 / (d + uRadius * 0.35);
  float trail = uMove * (core * 0.75 + tail * tail * 0.25);
  c.r += trail * (1.0 - c.r);

  float db = length(q - uBurst.xy * aspect);
  float burst = uBurst.z * exp(-db * db / (uBurst.w * uBurst.w));
  c.g += burst * (1.0 - c.g);
  c.r += burst * 0.5 * (1.0 - c.r);

  outColor = c;
}`;

  const MAX_DPR = 1.25; // the lights' canvases: soft glow, no need for full retina resolution
  const LIGHT_SIZE = 0.45; // the pointer light below the hero (the hero's is HERO_LIGHT_SIZE)

  const TRAIL_DECAY_PER_FRAME = 0.972; // at 60fps: light lingers ~1.5s
  const TRAIL_RADIUS = 0.05; // in canvas heights...
  const TRAIL_RADIUS_PER_WIDTH = 0.065; // ...but at most this much of the width, so it's smaller on phones
  const BURST_DURATION_S = 0.28;

  function compileProgram(gl, fragment) {
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || 'shader compile failed');
      }
      return shader;
    };
    const program = gl.createProgram();
    const vs = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl.FRAGMENT_SHADER, fragment);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program) || 'program link failed');
    }
    const uniforms = new Map();
    const u = (name) => {
      if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
      return uniforms.get(name);
    };
    return { program, u };
  }

  function createTexture(gl, wrap = gl.CLAMP_TO_EDGE) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  let webGL2Supported;

  /** Probed once: each probe is a WebGL context, and browsers only keep a handful alive. */
  function isWebGL2Supported() {
    if (webGL2Supported === undefined) {
      try {
        const gl = document.createElement('canvas').getContext('webgl2');
        webGL2Supported = !!gl;
        gl?.getExtension('WEBGL_lose_context')?.loseContext();
      } catch {
        webGL2Supported = false;
      }
    }
    return webGL2Supported;
  }

  /** Pointer light trail rendered into a texture; the owner composites it. Positions are uv, y up. */
  class LightTrail {
    /** `size` scales the light's radius (and its click burst), e.g. to suit a smaller cursor. */
    constructor(gl, { size = 1 } = {}) {
      this.gl = gl;
      this.size = size;
      this.program = compileProgram(gl, TRAIL_SHADER);
      this.targets = [];
      this.index = 0;
      this.width = 1;
      this.height = 1;
      this.pointer = null;
      this.lastPointer = null;
      this.burst = null;
      this.boost = 0;
      for (let i = 0; i < 2; i++) {
        this.targets.push({ texture: createTexture(gl), fbo: gl.createFramebuffer() });
      }
    }

    /** Size of the owner's canvas in device pixels; the trail runs at half of it. */
    resize(canvasWidth, canvasHeight) {
      const gl = this.gl;
      this.width = Math.max(1, Math.round(canvasWidth / 2));
      this.height = Math.max(1, Math.round(canvasHeight / 2));
      for (const { texture, fbo } of this.targets) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.viewport(0, 0, this.width, this.height);
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    pointerMove(x, y) {
      this.pointer = { x, y };
    }

    pointerLeave() {
      this.pointer = null;
      this.lastPointer = null;
    }

    /**
     * Light at the pointer this frame although it didn't move (0..1), e.g. while the page scrolls
     * under it and the content there stays put (a pinned section).
     */
    glow(strength) {
      this.boost = Math.max(this.boost, Math.min(strength, 1));
    }

    /** Click / tap: a stronger, grainier glow radiating from where it landed. */
    pointerDown(x, y) {
      this.pointer = { x, y };
      this.lastPointer = this.pointer;
      this.burst = { x, y, age: 0 };
    }

    /** Advance one frame; returns the texture holding the new trail. Leaves the default framebuffer bound. */
    update(dt, time, aspect, shift = 0) {
      const gl = this.gl;
      const { program, u } = this.program;
      const prev = this.targets[this.index];
      const next = this.targets[1 - this.index];
      this.index = 1 - this.index;

      gl.bindFramebuffer(gl.FRAMEBUFFER, next.fbo);
      gl.viewport(0, 0, this.width, this.height);
      gl.useProgram(program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, prev.texture);
      gl.uniform1i(u('uPrev'), 0);
      gl.uniform2f(u('uTexel'), 1 / this.width, 1 / this.height);
      gl.uniform1f(u('uAspect'), aspect);
      gl.uniform1f(u('uTime'), time);
      gl.uniform1f(u('uDecay'), Math.pow(TRAIL_DECAY_PER_FRAME, dt * 60));
      gl.uniform1f(u('uShift'), shift);
      const radius = Math.min(TRAIL_RADIUS, TRAIL_RADIUS_PER_WIDTH * aspect) * this.size;
      gl.uniform1f(u('uRadius'), radius);

      // The light drawn last frame has moved with the content by `shift`, so the new stretch starts
      // there: a pointer resting while the page scrolls leaves a streak along the content.
      const from = this.lastPointer ? { x: this.lastPointer.x, y: this.lastPointer.y + shift } : this.pointer;
      const to = this.pointer;
      let move = 0;
      if (from && to && this.lastPointer) {
        const moved = Math.hypot((to.x - this.lastPointer.x) * aspect, to.y - this.lastPointer.y);
        const pointerMove = moved > 0.0005 ? Math.min(0.2 + moved * 7, 1) : 0;
        // scrolling alone draws a softer light than moving the pointer
        const scrollMove = Math.abs(shift) > 0.0005 ? Math.min(0.25 + Math.abs(shift) * 5, 0.8) : 0;
        move = Math.max(pointerMove, scrollMove);
      }
      if (to) move = Math.max(move, this.boost);
      this.boost = 0;
      gl.uniform2f(u('uFrom'), from ? from.x : -1, from ? from.y : -1);
      gl.uniform2f(u('uTo'), to ? to.x : -1, to ? to.y : -1);
      gl.uniform1f(u('uMove'), move);
      this.lastPointer = this.pointer;

      if (this.burst) {
        this.burst.age += dt;
        const t = Math.min(this.burst.age / BURST_DURATION_S, 1);
        gl.uniform4f(u('uBurst'), this.burst.x, this.burst.y, 0.55 * (1 - t), radius * (1.2 + 2.6 * t));
        if (this.burst.age > BURST_DURATION_S) this.burst = null;
      } else {
        gl.uniform4f(u('uBurst'), -1, -1, 0, 1);
      }
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return next.texture;
    }

    destroy() {
      const gl = this.gl;
      gl.deleteProgram(this.program.program);
      for (const { texture, fbo } of this.targets) {
        gl.deleteTexture(texture);
        gl.deleteFramebuffer(fbo);
      }
    }
  }

  // --- ambient light

  const AMBIENT_SCALE = 0.25; // of the owner's canvas
  const DISPLACEMENT_RANGE = 0.35; // a stored value of 1 moves the light this far (canvas heights)
  const RELAX_PER_FRAME = 0.955; // at 60fps: pushed light drifts back within ~1s
  const PUSH_RADIUS = 0.13; // in canvas heights (capped by width on phones, like the trail)
  const PUSH_RADIUS_PER_WIDTH = 0.2;
  const AMBIENT_BURST_DURATION_S = 0.35;

  const NOISE_GLSL = `
float vhash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(vhash(i), vhash(i + vec2(1.0, 0.0)), u.x),
    mix(vhash(i + vec2(0.0, 1.0)), vhash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}
float fbm(vec2 p) {
  float sum = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    sum += amp * vnoise(p);
    p = mat2(1.6, 1.2, -1.2, 1.6) * p + 7.3;
    amp *= 0.5;
  }
  return sum;
}`;

  const DISPLACEMENT_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uPrev;
uniform vec2 uTexel;
uniform float uAspect;
uniform float uRelax;
uniform float uShift; // content scroll since last frame, in uv (see LightTrail)
uniform vec2 uFrom;
uniform vec2 uTo;
uniform float uRadius;
uniform float uActive; // 0 while no pointer
uniform vec4 uBurst; // xy position, z strength, w radius

vec2 field(vec2 uv) { return texture(uPrev, uv).rg * 2.0 - 1.0; }

float segmentDistance(vec2 p, vec2 a, vec2 b, out float h) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
  return length(pa - ba * h);
}

void main() {
  vec2 aspect = vec2(uAspect, 1.0);
  vec2 uv = vUv - vec2(0.0, uShift);
  // spread out a little and relax back towards rest
  vec2 d = field(uv) * 0.5
    + (field(uv + vec2(uTexel.x, 0.0)) + field(uv - vec2(uTexel.x, 0.0))
      + field(uv + vec2(0.0, uTexel.y)) + field(uv - vec2(0.0, uTexel.y))) * 0.125;
  d *= uRelax;

  vec2 q = vUv * aspect;
  vec2 a = uFrom * aspect;
  vec2 b = uTo * aspect;
  float h;
  float dist = segmentDistance(q, a, b, h);
  float w = exp(-dist * dist / (uRadius * uRadius)) * uActive;
  vec2 motion = b - a;
  float speed = length(motion);
  // drag the light along the pointer's path...
  d += motion * w * ${(1.6 / DISPLACEMENT_RANGE).toFixed(3)};
  // ...and shove it aside, away from the path
  vec2 away = q - (a + (b - a) * h);
  float awayLength = length(away);
  if (awayLength > 1e-5)
    d += away / awayLength * w * min(speed * ${(9 / DISPLACEMENT_RANGE).toFixed(3)}, 0.6);

  // click / tap: throw the light outwards in a ring
  vec2 fromBurst = q - uBurst.xy * aspect;
  float burstDistance = length(fromBurst);
  if (burstDistance > 1e-5) {
    float ring = exp(-burstDistance * burstDistance / (uBurst.w * uBurst.w));
    d += fromBurst / burstDistance * ring * uBurst.z;
  }

  outColor = vec4(clamp(d, -1.0, 1.0) * 0.5 + 0.5, 0.0, 1.0);
}`;

  const LIGHT_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uDisplacement;
uniform float uAspect;
uniform float uTime;
uniform vec2 uOffset; // e.g. scroll parallax, in canvas heights
${NOISE_GLSL}

void main() {
  vec2 displacement = (texture(uDisplacement, vUv).rg * 2.0 - 1.0) * ${DISPLACEMENT_RANGE.toFixed(3)};
  // light that was pushed away from here is sampled from where it came from
  vec2 p = vUv * vec2(uAspect, 1.0) - displacement + uOffset;
  float t = uTime;

  // broad bands, bent by slowly drifting noise: one set sweeps diagonally (~40s across), a second,
  // dimmer set crosses it the other way, so there's (almost) always some light on screen
  vec2 warp = vec2(fbm(p * 0.9 + vec2(t * 0.035, -t * 0.02)), fbm(p * 0.9 + vec2(-t * 0.025, t * 0.03) + 4.7));
  float bands = sin(dot(p, vec2(0.62, 0.78)) * 3.1 + (warp.x - 0.5) * 5.5 + (warp.y - 0.5) * 2.5 - t * 0.16);
  float crossBands = sin(dot(p, vec2(-0.8, 0.6)) * 2.48 + (warp.y - 0.5) * 5.0 + (warp.x - 0.5) * 2.0 + t * 0.128 + 2.1);
  // mostly dark: only the crest of each band lights up, with a soft, uneven body so they come and go
  float light = pow(smoothstep(0.6, 1.0, bands * 0.5 + 0.5), 1.6)
    * smoothstep(0.32, 0.7, fbm(p * 1.3 - vec2(t * 0.04, t * 0.015) + 11.0));
  float crossLight = pow(smoothstep(0.6, 1.0, crossBands * 0.5 + 0.5), 1.6)
    * smoothstep(0.32, 0.7, fbm(p * 1.1 + vec2(t * 0.03, t * 0.02) + vec2(23.0, 5.0)));
  light = max(light, crossLight * 0.85);
  // where the light was shoved aside it thins out, leaving a darker wake
  light *= 1.0 - 0.55 * clamp(length(displacement) / ${DISPLACEMENT_RANGE.toFixed(3)} * 1.6, 0.0, 1.0);
  outColor = vec4(light, 0.0, 0.0, 1.0);
}`;

  class AmbientLight {
    constructor(gl) {
      this.gl = gl;
      this.displacementProgram = compileProgram(gl, DISPLACEMENT_SHADER);
      this.lightProgram = compileProgram(gl, LIGHT_SHADER);
      const target = () => ({ texture: createTexture(gl), fbo: gl.createFramebuffer() });
      this.displacement = [target(), target()];
      this.light = target();
      this.index = 0;
      this.width = 1;
      this.height = 1;
      this.pointer = null;
      this.lastPointer = null;
      this.burst = null;
    }

    /** Size of the owner's canvas in device pixels; the light runs at a quarter of it. */
    resize(canvasWidth, canvasHeight) {
      const gl = this.gl;
      this.width = Math.max(1, Math.round(canvasWidth * AMBIENT_SCALE));
      this.height = Math.max(1, Math.round(canvasHeight * AMBIENT_SCALE));
      const targets = [...this.displacement.map((t) => [t, true]), [this.light, false]];
      for (const [{ texture, fbo }, rest] of targets) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.viewport(0, 0, this.width, this.height);
        // displacement at rest is 0.5 (the bias)
        if (rest) gl.clearColor(0.5, 0.5, 0, 1);
        else gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    /** Positions are uv, y up (like LightTrail). */
    pointerMove(x, y) {
      this.pointer = { x, y };
    }

    pointerLeave() {
      this.pointer = null;
      this.lastPointer = null;
    }

    pointerDown(x, y) {
      this.pointer = { x, y };
      this.burst = { x, y, age: 0 };
    }

    /**
     * Advance one frame; returns the texture holding the light (R, 0..~1). `shift` is the content
     * scroll since the last frame in uv (so pushed light stays with the content), `offset` moves the
     * whole pattern (e.g. a little with the scroll). Leaves the default framebuffer bound.
     */
    update(dt, time, aspect, shift = 0, offset = { x: 0, y: 0 }) {
      const gl = this.gl;

      // 1. displacement
      {
        const { program, u } = this.displacementProgram;
        const prev = this.displacement[this.index];
        const next = this.displacement[1 - this.index];
        this.index = 1 - this.index;
        gl.bindFramebuffer(gl.FRAMEBUFFER, next.fbo);
        gl.viewport(0, 0, this.width, this.height);
        gl.useProgram(program);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, prev.texture);
        gl.uniform1i(u('uPrev'), 0);
        gl.uniform2f(u('uTexel'), 1 / this.width, 1 / this.height);
        gl.uniform1f(u('uAspect'), aspect);
        gl.uniform1f(u('uRelax'), Math.pow(RELAX_PER_FRAME, dt * 60));
        gl.uniform1f(u('uShift'), shift);
        const radius = Math.min(PUSH_RADIUS, PUSH_RADIUS_PER_WIDTH * aspect);
        gl.uniform1f(u('uRadius'), radius);
        const to = this.pointer;
        const from = this.lastPointer ? { x: this.lastPointer.x, y: this.lastPointer.y + shift } : to;
        gl.uniform1f(u('uActive'), to && this.lastPointer ? 1 : 0);
        gl.uniform2f(u('uFrom'), from ? from.x : -1, from ? from.y : -1);
        gl.uniform2f(u('uTo'), to ? to.x : -1, to ? to.y : -1);
        this.lastPointer = this.pointer;
        if (this.burst) {
          this.burst.age += dt;
          const t = Math.min(this.burst.age / AMBIENT_BURST_DURATION_S, 1);
          // a ring that travels outwards and fades
          gl.uniform4f(u('uBurst'), this.burst.x, this.burst.y, 0.22 * (1 - t) * (dt * 60), radius * (0.8 + 2.2 * t));
          if (this.burst.age > AMBIENT_BURST_DURATION_S) this.burst = null;
        } else {
          gl.uniform4f(u('uBurst'), -1, -1, 0, 1);
        }
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }

      // 2. light
      {
        const { program, u } = this.lightProgram;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.light.fbo);
        gl.viewport(0, 0, this.width, this.height);
        gl.useProgram(program);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.displacement[this.index].texture);
        gl.uniform1i(u('uDisplacement'), 0);
        gl.uniform1f(u('uAspect'), aspect);
        gl.uniform1f(u('uTime'), time);
        gl.uniform2f(u('uOffset'), offset.x, offset.y);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }

      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return this.light.texture;
    }

    destroy() {
      const gl = this.gl;
      gl.deleteProgram(this.displacementProgram.program);
      gl.deleteProgram(this.lightProgram.program);
      for (const { texture, fbo } of [...this.displacement, this.light]) {
        gl.deleteTexture(texture);
        gl.deleteFramebuffer(fbo);
      }
    }
  }

  window.OudGL = {
    HASH_GLSL,
    TRAIL_GLOW_GLSL,
    AMBIENT_GRAIN_GLSL,
    MAX_DPR,
    LIGHT_SIZE,
    compileProgram,
    createTexture,
    isWebGL2Supported,
    LightTrail,
    AmbientLight,
  };
})();
